/**
 * reviewCountSignal — "the open-review count may have changed" (BON-381 U14).
 *
 * The rail badge lives in the app chrome and fetched its count once, on mount.
 * The detail page lives inside that chrome. So resolving the last flagged item
 * left the badge advertising work that no longer existed until a full reload —
 * and the badge is the whole reason an operator visits this feature at all, so a
 * stale one is worse than none.
 *
 * A three-line pub/sub rather than a query cache or a context: the two ends are
 * a hook and a hook, they never render together, and the only thing that needs to
 * cross between them is a nudge with no payload. Anything with state in it would
 * be a second source of truth for a number the server already owns.
 *
 * Module-level on purpose — the subscriber is mounted once in the chrome and the
 * publisher may be anywhere below it, so there is no common ancestor to hang a
 * provider on that is not simply "the app".
 */
type Listener = () => void;

const listeners = new Set<Listener>();

/** Subscribe; returns the unsubscribe, shaped for a `useEffect` cleanup. */
export function onReviewCountStale(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Announce that the count is stale. Called after a transition COMMITS — never
 * optimistically, because the badge must show what the server thinks rather than
 * what we hoped, and a refused resolve must not move it.
 */
export function markReviewCountStale(): void {
  for (const listener of listeners) listener();
}
